import React, { Dispatch, SetStateAction, useState } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { useFormik } from "formik";
import { ActionHandlerFunction, AdminUser, OrgInfo } from "@preveil-api";
import {
  Account, AddUsersMessaging, AdminErrorMessages, AdminSuccessMessages, AdminUIActionTypes, Message, MessageAnchors, MessageHandlerDisplayType,
  OrgInviteEntity, RegexHelper, findDuplicateAddress, isSameUser, useAddOrganizationMembersMutation, useAppDispatch, useOrgSubsumeMemberMutation, usePostUsersFindMutation
} from "src/common";
import { UserInfoCard } from "..";
import { adminActions, uiActions } from "src/store";
import * as Yup from "yup";

type AllProps = {
  users_list: string[];
  handleAction: ActionHandlerFunction;
  current_account: Account;
  setShowModal: Dispatch<SetStateAction<boolean>>;
  local_users_accounts_map: Map<string, Account> | undefined;
  org_info: OrgInfo;
}

function AddSingleUserFormComponent(props: AllProps) {
  const { users_list, handleAction, current_account, local_users_accounts_map, setShowModal, org_info } = props;
  const dispatch = useAppDispatch();
  const [subsume_account, setSubsumeAccount] = useState<AdminUser>();
  const [show_existing_account_step, setShowExistingAccountStep] = useState<boolean>(false);
  const [display_name_not_matched, setDisplayNameNotMatched] = useState<boolean>(false);

  const [findUsers] = usePostUsersFindMutation();
  const [addOrganizationMembers] = useAddOrganizationMembersMutation();
  const [orgSubsumeMember] = useOrgSubsumeMemberMutation();

  const formik = useFormik({
    validationSchema: Yup.object().shape({
      email: Yup
        .string()
        .required("The email address is required.")
        .test("user-email", AddUsersMessaging.invalid_email_message, (value) => {
          if (value) {
            return RegexHelper.testEmailAddress(value);
          }
          return !!value;
        })
        .test("user-email", AddUsersMessaging.default, (value: any) => {
          if (value) {
            const _value = value.toLowerCase();
            return !findDuplicateAddress(users_list, [_value]);
          }
          return !!value;
        }),
      first_name: Yup
        .string()
        .required("The first name is required."),
      last_name: Yup
        .string()
        .required("The last name is required."),
      department: Yup
        .string()
        .required("The department is required.")
    }),
    initialValues: {
      email: "",
      first_name: "",
      last_name: "",
      department: "",
    },
    onSubmit: () => { } // required for useFormik hook, but we are not using onSubmit.
  });

  function handleReset() {
    formik.resetForm();
    reset();
  }

  function handleSubmit() {
    submitUserData(formik.values);
    formik.resetForm();
  }

  // Description: Get the user data from the API.
  // We are using the findUsers API to see if the user is an express subsume account.
  async function getUser(user_ids: string[]) {
    if (current_account && user_ids.length > 0) {
      const users = user_ids.map((userId) => {
        return { user_id: userId, key_version: -1 };
      });
      return await findUsers({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: { spec: users }
      })
        .unwrap()
        .then((response) => {
          const { users, errors } = response;
          return { users, errors };
        })
        .catch((error) => {
          handleAction({ actionType: AdminUIActionTypes.PageErrorMessage, params: { message: AdminErrorMessages.error_finding_user_accounts_from_csv, stack: error } });
        });
    }
  }

  // Description: This is for the individual user invite in the modal.
  // We are checking if the user is an express subsume account, full subsume account or a new/external account.
  async function submitUserData(user: OrgInviteEntity) {
    const { email, first_name, last_name, department } = user;
    const is_subsume = local_users_accounts_map?.has(user.email);
    const _user_invite = {
      user_id: email.toLowerCase(),
      display_name: `${first_name} ${last_name}`,
      role: "standard",
      department,
      devices: [],
      status: "invited"
    };
    if (is_subsume) {
      const original_display_name = local_users_accounts_map?.get(user.email)?.display_name;
      !isSameUser(_user_invite.display_name, original_display_name) && setDisplayNameNotMatched(true);
      _user_invite.display_name = original_display_name || _user_invite.display_name;
      setSubsumeAccount(_user_invite);
      setShowExistingAccountStep(true);
    } else {
      const { email } = user;
      const response = await getUser([email]);
      if (!!response && response.users.length > 0) {
        const { users } = response;
        if (users.length === 1 && isSameUser(users[0].user_id, email)) {
          !isSameUser(_user_invite.display_name, users[0].display_name) && setDisplayNameNotMatched(true);
          _user_invite.display_name = users[0].display_name || _user_invite.display_name;
          setSubsumeAccount(_user_invite);
          setShowExistingAccountStep(true);
        }
      } else {
        submitUserInvite(_user_invite);
      }
    }
  }

  // Description: API for sending invites to external users.
  async function submitUserInvite(_user_invite: AdminUser) {
    if (!!org_info) {
      const userId = current_account.user_id;
      const orgId = org_info.org_id;
      await addOrganizationMembers({ orgId, userId, users: [_user_invite] })
        .unwrap()
        .then(() => {
          const success_message = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${_user_invite.display_name}`);
          handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
          dispatch(adminActions.setSelected([]));
          dispatch(uiActions.handleSetMessage(new Message(success_message)));
          setShowModal(false);
        })
        .catch((error) => {
          const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.message_content, `${_user_invite.display_name}`);
          dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
        });
    }
  }

  // Description: API for sending invites to registered users.
  // Note: You can only resend invites to users that have a "pending" status.
  // And subsume accounts only get "invited" status.
  async function sendSubsumeInvite() {
    if (!!subsume_account) {
      setDisplayNameNotMatched(false);
      setShowExistingAccountStep(false);
      setSubsumeAccount(undefined);
      const orgId = org_info.org_id;
      const { user_id, department } = subsume_account;
      await orgSubsumeMember({
        orgId,
        userId: current_account.user_id,
        subsumeUserId: user_id,
        department
      })
        .unwrap()
        .then(() => {
          const successMessage = AdminSuccessMessages.success_invite_user.replace(MessageAnchors.message_content, `${user_id}`);
          handleAction({ actionType: AdminUIActionTypes.GetRefreshRequest });
          dispatch(uiActions.handleSetMessage(new Message(successMessage)));
        })
        .catch((error) => {
          const error_message = AdminErrorMessages.error_invite_user_to_org.replace(MessageAnchors.user_id, `${user_id}`);
          dispatch(uiActions.handleRequestErrors(new Message(error_message, MessageHandlerDisplayType.toastr), error));
        });
    }
  }

  function cancel() {
    setShowModal(false);
    reset();
  }

  function reset() {
    setDisplayNameNotMatched(false);
    setShowExistingAccountStep(false);
    setSubsumeAccount(undefined);
  }

  return <>{!show_existing_account_step ? <Form noValidate className="m-2">
    <Row>
      <Col xs="6">
        <Form.Group>
          <Form.Label htmlFor="email_tb">Email Address</Form.Label>
          <Form.Control
            id="email_tb"
            type="text"
            name="user-email"
            onChange={formik.handleChange("email")}
            onBlur={formik.handleBlur("email")}
            value={formik.values.email}
            isInvalid={formik.touched.email && !!formik.errors.email}
          />
          {formik.touched.email && !!formik.errors.email ? (
            <p className="error-message">{formik.errors.email}</p>
          ) : null}
        </Form.Group>
      </Col>
    </Row>
    <Row className="mt-4">
      <Col xs="6">
        <Form.Group>
          <Form.Label htmlFor="first_name_tb">First Name</Form.Label>
          <Form.Control
            id="first_name_tb"
            type="text"
            name="first-name"
            onChange={formik.handleChange("first_name")}
            onBlur={formik.handleBlur("first_name")}
            value={formik.values.first_name}
            isInvalid={formik.touched.first_name && !!formik.errors.first_name}
          />
          {formik.touched.first_name && !!formik.errors.first_name ? (
            <p className="error-message">{formik.errors.first_name}</p>
          ) : null}
        </Form.Group>
      </Col>
      <Col xs="6">
        <Form.Group>
          <Form.Label htmlFor="last_name_tb">Last Name</Form.Label>
          <Form.Control
            id="last_name_tb"
            type="text"
            name="last-name"
            onChange={formik.handleChange("last_name")}
            onBlur={formik.handleBlur("last_name")}
            value={formik.values.last_name}
            isInvalid={formik.touched.last_name && !!formik.errors.last_name}
          />
          {formik.touched.last_name && !!formik.errors.last_name ? (
            <p className="error-message">{formik.errors.last_name}</p>
          ) : null}
        </Form.Group>
      </Col>
    </Row>
    <Row className="mt-4">
      <Col xs="6">
        <Form.Group>
          <Form.Label htmlFor="department_tb">Department</Form.Label>
          <Form.Control
            id="department_tb"
            type="text"
            name="user-department"
            onChange={formik.handleChange("department")}
            onBlur={formik.handleBlur("department")}
            value={formik.values.department}
            isInvalid={formik.touched.department && !!formik.errors.department}
          />
          {formik.touched.department && !!formik.errors.department ? (
            <p className="error-message">{formik.errors.department}</p>
          ) : null}
        </Form.Group>
      </Col>
    </Row>
    <div className="btn-panel mt-4">
      <Button id="add_user" disabled={!formik.isValid || Object.keys(formik.touched).length === 0} onClick={handleSubmit}>Create Account</Button>
      <Button id="reset_form" disabled={!formik.touched} variant="no-outline-primary" onClick={handleReset}>Reset</Button>
      <Button id="cancel_form" variant="no-outline-primary" onClick={cancel}>Cancel</Button>
    </div>
  </Form> :
    <>{!!subsume_account && <>
      <div className={display_name_not_matched ? "" : "mt-3 mb-3"}>
        <>{display_name_not_matched ? (
          <>
            <span className="fs-2">The user {subsume_account.user_id} already exists.</span> 
            <p>Select <b className="text-primary"> Cancel </b> if this is not the user you intended to add. </p> 
          </>
          ) : <h3>Existing PreVeil Account</h3>}</> 
        <UserInfoCard users={[subsume_account]} />
      </div>
      <div className="btn-panel">
        <Button onClick={() => sendSubsumeInvite()}>Invite</Button>
        <Button variant="no-outline-primary" onClick={reset}>Reset</Button>
        <Button variant="no-outline-primary" onClick={cancel}>Cancel</Button>
      </div></>}
    </>}
  </>;
}

export default React.memo(AddSingleUserFormComponent);
